/** Selects expected payment countries and connects the selection to the new-account form. */
import { SyntheticEvent, useState } from 'react';
import { Autocomplete, Checkbox, FormControl, FormHelperText, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { CustomTextField } from 'theme/elements/Inputs';
import { maxExpectedPaymentCountries } from 'constants/new_accounts.constants';
import { customComponentInternalProps } from 'components/FormCreatorComponent/types';
import { getCountries } from 'state/reducers/global.reducer';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

type ExpectedPaymentCountryOption = {
	id: number;
	label: string;
	isForbidden?: boolean;
};

type props = {
	value: number[];
	onChange: (value: number[]) => void;
	options: ExpectedPaymentCountryOption[];
	disabled?: boolean;
	valid?: boolean;
};

export default function ExpectedPaymentCountriesPicker(props: props) {
	const t = useAppSelector(selectTranslations);
	const { value, onChange, options, disabled = false, valid = true } = props;
	const [isLimitReached, setIsLimitReached] = useState(false);
	const selectedOptions = options.filter((option) => value.includes(option.id));

	const handleChange = (_event: SyntheticEvent, nextOptions: ExpectedPaymentCountryOption[]) => {
		if (nextOptions.length > maxExpectedPaymentCountries) {
			setIsLimitReached(true);
			return;
		}
		setIsLimitReached(false);
		onChange(nextOptions.map((option) => option.id));
	};

	return (
		<CustomTextField>
			<FormControl error={isLimitReached || !valid} fullWidth>
				<Autocomplete
					multiple
					disableCloseOnSelect
					ListboxProps={{ dir: 'rtl' }}
					options={options}
					value={selectedOptions}
					getOptionLabel={(option) => option.label}
					isOptionEqualToValue={(option, selected) => option.id === selected.id}
					onChange={handleChange}
					disabled={disabled}
					renderOption={(props, option, { selected }) => {
						const { key, ...optionProps } = props;
						return (
							<li key={key} {...optionProps}>
								<Checkbox icon={icon} checkedIcon={checkedIcon} sx={{ mr: 1 }} checked={selected} />
								{option.label}
							</li>
						);
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label={t('page.accounts.new_account.customer.expected_payment_countries')}
							variant="filled"
							required
							error={isLimitReached || !valid}
						/>
					)}
				/>
				{isLimitReached && (
					<FormHelperText>
						{t('page.accounts.new_account.customer.expected_payment_countries.max')}
					</FormHelperText>
				)}
			</FormControl>
		</CustomTextField>
	);
}

export function ExpectedPaymentCountriesFormField(props: customComponentInternalProps) {
	const { valid, cField, handleChange } = props;
	const countries = useAppSelector(getCountries);
	const options = (countries || []).filter(
		(country: ExpectedPaymentCountryOption) => country.isForbidden === false,
	);

	return (
		<ExpectedPaymentCountriesPicker
			value={Array.isArray(cField.props.value) ? cField.props.value : []}
			onChange={(next) => handleChange({ target: { value: next } })}
			options={options}
			disabled={cField.props.disabled}
			valid={valid}
		/>
	);
}
